import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Mail, Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

export default function Login() {
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(null); // null | 'request' | 'verify'
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };

  const handleRequestReset = async () => {
    setResetError(''); setResetMessage('');
    if (!resetEmail) { setResetError('Enter your email address'); return; }
    setResetLoading(true);
    try {
      const res = await api.post('/auth/request-reset', { email: resetEmail });
      setResetMessage(res.data.message);
      setResetMode('verify');
    } catch (err) {
      setResetError(err.response?.data?.detail || 'Failed to send reset code');
    }
    setResetLoading(false);
  };

  const handleResetPassword = async () => {
    setResetError(''); setResetMessage('');
    if (!resetCode || !newPassword) { setResetError('Enter the code and your new password'); return; }
    if (newPassword.length < 6) { setResetError('Password must be at least 6 characters'); return; }
    setResetLoading(true);
    try {
      const res = await api.post('/auth/reset-password', { email: resetEmail, code: resetCode, new_password: newPassword });
      setResetMessage(res.data.message);
      setResetMode(null);
      setEmail(resetEmail);
    } catch (err) {
      setResetError(err.response?.data?.detail || 'Reset failed');
    }
    setResetLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 animate-fade-in-up">
          <Link to="/" className="inline-block">
            <img src="https://customer-assets.emergentagent.com/job_lamnso-hub/artifacts/up9h807k_7ba9c841-3c9f-45da-b619-44a6f0b3d5bf.JPG" alt="LámFon" className="h-20 w-auto mx-auto mb-2" data-testid="login-logo" />
          </Link>
          <p className="mt-2 text-sm text-[#57534E]">Sign in to your account</p>
        </div>

        <div className="bg-white rounded-2xl border border-[#E7E5E4] shadow-sm p-6 sm:p-8 animate-fade-in-up stagger-1">
          {error && (
            <div className="mb-4 p-3 bg-[#E8F5E9] border border-[#4CAF50]/20 rounded-xl text-sm text-[#4CAF50]" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#1C1917] mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29E]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full rounded-xl border-0 py-3 pl-10 pr-4 text-[#1C1917] shadow-sm ring-1 ring-inset ring-[#E7E5E4] placeholder:text-[#A8A29E] focus:ring-2 focus:ring-inset focus:ring-[#4CAF50] sm:text-sm bg-white"
                  placeholder="your@email.com"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1C1917] mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29E]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full rounded-xl border-0 py-3 pl-10 pr-10 text-[#1C1917] shadow-sm ring-1 ring-inset ring-[#E7E5E4] placeholder:text-[#A8A29E] focus:ring-2 focus:ring-inset focus:ring-[#4CAF50] sm:text-sm bg-white"
                  placeholder="Enter password"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A8A29E] hover:text-[#57534E]"
                  data-testid="login-toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div />
              <button type="button" onClick={() => { setResetMode('request'); setResetEmail(email); }}
                className="text-xs text-[#4CAF50] hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </button>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl py-3 font-semibold"
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Password Reset Flow */}
          {resetMode && (
            <div className="mt-5 p-4 bg-[#F5F5F4] rounded-xl" data-testid="reset-password-form">
              <button onClick={() => { setResetMode(null); setResetError(''); setResetMessage(''); }}
                className="flex items-center gap-1 text-xs text-[#57534E] hover:text-[#1C1917] mb-3">
                <ArrowLeft className="w-3 h-3" /> Back to sign in
              </button>

              {resetMessage && <p className="text-xs text-[#588157] bg-[#EAF1EA] p-2 rounded-lg mb-3">{resetMessage}</p>}
              {resetError && <p className="text-xs text-[#4CAF50] bg-[#E8F5E9] p-2 rounded-lg mb-3">{resetError}</p>}

              {resetMode === 'request' && (
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-[#1C1917]">Reset your password</p>
                  <p className="text-xs text-[#57534E]">Enter your email and we'll send you a 6-digit code.</p>
                  <input type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full rounded-xl border-0 py-2.5 px-3 text-sm ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50] bg-white"
                    data-testid="reset-email-input" />
                  <Button onClick={handleRequestReset} disabled={resetLoading}
                    className="w-full bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl" data-testid="send-reset-code-btn">
                    {resetLoading ? 'Sending...' : 'Send Reset Code'}
                  </Button>
                </div>
              )}

              {resetMode === 'verify' && (
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-[#1C1917]">Enter your reset code</p>
                  <p className="text-xs text-[#57534E]">Check your email for a 6-digit code.</p>
                  <input type="text" value={resetCode} onChange={(e) => setResetCode(e.target.value)}
                    placeholder="6-digit code" maxLength={6}
                    className="w-full rounded-xl border-0 py-2.5 px-3 text-center text-lg font-bold tracking-[8px] ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50] bg-white"
                    data-testid="reset-code-input" />
                  <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password (min 6 characters)"
                    className="w-full rounded-xl border-0 py-2.5 px-3 text-sm ring-1 ring-inset ring-[#E7E5E4] focus:ring-[#4CAF50] bg-white"
                    data-testid="reset-new-password-input" />
                  <Button onClick={handleResetPassword} disabled={resetLoading}
                    className="w-full bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl" data-testid="reset-password-btn">
                    {resetLoading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="mt-5">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#E7E5E4]" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 bg-white text-[#A8A29E]">or continue with</span>
              </div>
            </div>

            <button
              onClick={loginWithGoogle}
              className="mt-4 w-full flex items-center justify-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-[#1C1917] ring-1 ring-inset ring-[#E7E5E4] hover:bg-[#F5F5F4] transition-colors"
              data-testid="login-google-button"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-[#57534E]">
          Don't have an account?{' '}
          <Link to="/register" className="text-[#4CAF50] font-semibold hover:underline" data-testid="login-register-link">
            Sign up
          </Link>
        </p>

        <p className="mt-3 text-center text-[10px] text-[#A8A29E]">
          Need help? <a href="mailto:gills.fai@lamfon.app" className="text-[#4CAF50] hover:underline">Email</a> or <a href="https://wa.me/17634616615" target="_blank" rel="noopener noreferrer" className="text-[#4CAF50] hover:underline">WhatsApp</a>
        </p>
      </div>
    </div>
  );
}
